import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthProvider } from "./auth/AuthContext";
import App from "./App";
import "./styles/global.css";

const NEW_UI =
  String(import.meta.env.VITE_NEW_UI ?? "true").toLowerCase() === "true" ||
  String(import.meta.env.VITE_NEW_UI ?? "true") === "1";

// Owen 2026-05-29 (Ship 11 / Lever 1): route-condition the app-only CSS
// at boot. PSI flagged foreman-ui-app.css as 18 KB unused on marketing
// pages — pure dead weight on the LCP critical path. We check the
// initial pathname and only load app/admin CSS for non-marketing routes.
// SPA navigation from marketing → app is handled by AppCssLoader in
// App.tsx (loads on demand when the route changes).
function isMarketingPath(path: string): boolean {
  return (
    path === "/" ||
    path.startsWith("/articles") ||
    path === "/get-started" ||
    path === "/overview" ||
    path === "/privacy"
  );
}

if (NEW_UI) {
  const initialPath =
    typeof window !== "undefined" ? window.location.pathname : "/";
  const onMarketing = isMarketingPath(initialPath);
  // Parallel load via Promise.all so CSS chunks don't serialise on
  // throttled mobile. Order is preserved by cascade specificity, not
  // by load order. NO_FCP regression on Ship 2 was the lesson.
  const chunks = [
    import("./styles/foreman-ui.css"),
    import("./styles/foreman-kit.css"),
  ];
  if (!onMarketing) {
    chunks.push(
      import("./styles/foreman-ui-app.css"),
      import("./styles/foreman-ui-automations.css"),
    );
  }
  await Promise.all(chunks);
}

// Inspector for the Foreman Hub UI Hub — loaded only when the app is
// running in UI-only mode (i.e. inside the UI Hub's iframes). Lets the
// Hub's parent page postMessage us into element-picker mode for
// zero-vision-token handoffs to agents.
const UI_ONLY =
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "").toLowerCase() === "true" ||
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "") === "1";
if (UI_ONLY) {
  await import("./inspector");
}

import { initPostHog } from "./lib/posthog";
import { initSentry } from "./lib/sentry";

// ── Defer observability init off the critical path ──
// Owen 2026-05-28 (Levers 4 + 6): both init calls already lazy-load
// their vendor chunks via dynamic import inside the lib files.
// Wrapping in requestIdleCallback also defers the call site itself
// so neither blocks initial paint. setTimeout fallback for browsers
// without rIC (older Safari).
const deferIdle = (cb: () => void) => {
  if (typeof window === "undefined") return;
  if (typeof (window as Window & { requestIdleCallback?: unknown })
        .requestIdleCallback === "function") {
    (window as Window & {
      requestIdleCallback: (cb: () => void) => void;
    }).requestIdleCallback(cb);
  } else {
    setTimeout(cb, 1);
  }
};
deferIdle(() => initSentry());
deferIdle(() => initPostHog());

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AuthProvider>
      <App />
    </AuthProvider>
  </StrictMode>,
);
